import {
    ApplicationInsights,
    IEventTelemetry,
    IExceptionTelemetry,
} from '@microsoft/applicationinsights-web';
import { isRehydratedMessage, getUrlParam, getTenantName } from './helpers';
import { v4 as uuidv4 } from 'uuid';
import { Tier } from './config';
import { AmpFlagSet, amplitudeClients } from './amplitude';

let appInsights: ApplicationInsights | undefined = undefined;
let debugInsightsEnabled = false;

export const initAppInsights = (
    appInsightsKey: string | undefined,
    tier: Tier
) => {
    if (appInsightsKey && process.env.JEST_WORKER_ID === undefined) {
        // TODO: consider adding debug to config
        debugInsightsEnabled = tier !== 'prod';
        appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: appInsightsKey,
                disableFetchTracking: false,
                disableCorrelationHeaders: false,
                enableCorsCorrelation: true,
                distributedTracingMode: 1,
                correlationHeaderExcludedDomains: ['*.pure.cloud'],
                maxBatchInterval: 2000,
            },
        });
        appInsights.loadAppInsights();
    }
};

let conversationId: string;
let sessionId: string;
let correlationId: string;

export const getConversationId = (): string => {
    return conversationId;
};

export const getSessionId = (): string => {
    return sessionId;
};

export const getCorrelationId = (): string => {
    if (correlationId) {
        return correlationId;
    }
    const correlationIdPassedIn = getUrlParam('correlationId').toString();
    if (!correlationIdPassedIn) {
        correlationId = uuidv4().toString();
    } else {
        correlationId = correlationIdPassedIn;
    }
    setCorrelationId(correlationId);
    return correlationId;
};

export const setConversationId = (newConvId: string): void => {
    console.log(`conversationId: ${newConvId}`);
    conversationId = newConvId;
};

export const setSessionId = (newSessId: string): void => {
    console.log(`sessionId: ${newSessId}`);
    sessionId = newSessId;
};

export const setCorrelationId = (newCorrId: string): void => {
    console.log(`correlationId: ${newCorrId}`);
    correlationId = newCorrId;
};

export const updateCorrelationId = (
    featureFlags: AmpFlagSet,
    activity: {
        name: string;
        value?: any;
    },
    currWatermark?: string
): void => {
    if (isRehydratedMessage(featureFlags, currWatermark)) {
        return;
    }
    if (activity.name === 'loggingEvent') {
        const eventType = activity.value?.eventType;
        if (eventType && eventType === 'Correlation ID Update') {
            const correlationId =
                activity.value?.eventProperties?.correlationId;
            if (correlationId && correlationId !== '') {
                setCorrelationId(correlationId);
            }
        }
    }
};

export const addIds = (event: any): any => {
    if (!event.properties) {
        event.properties = {};
    }
    event.properties.conversationId = conversationId;
    event.properties.sessionId = sessionId;
    event.properties.correlationId = getCorrelationId();
    event.properties.location = 'grace-client';
    event.properties.tenant = getTenantName();
    event.properties.ampdid = amplitudeClients.getDeviceId();
    return event;
};

export const logEvent = (
    event: IEventTelemetry,
    customProperties?: any
): void => {
    appInsights?.trackEvent(addIds(event), customProperties);
};

export const logDebugInsights = (
    event: IEventTelemetry,
    customProperties?: unknown
) => {
    if (debugInsightsEnabled) {
        logEvent(event, customProperties);
    }
};

export const logException = (
    name: string,
    exception: IExceptionTelemetry
): void => {
    if (!exception.properties) {
        exception.properties = {};
    }
    exception.properties.name = name;
    appInsights?.trackException(addIds(exception));
};

export const flushInsightsAsync = () => {
    return new Promise<void>((resolve, reject) => {
        try {
            if (!appInsights) {
                throw new Error('App Insights instance is null or undefined.');
            }
            appInsights.flush(true);
            resolve();
        } catch (error: any) {
            logException('Message Assist - App Insights flush error', {
                exception: error,
            });
            reject(error);
        }
    });
};
